import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];

export default function UnitsOfMeasure() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/uom");
      setUoms(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (uom) => {
    try {
      await api.patch(`/masters/uom/${uom.id}`, { is_active: !uom.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="uom-page">
      <PageHeader
        title="Units of Measure"
        description="Standard measurement units used across procurement, inventory and bills."
        action={canManage && <CreateUomDialog onCreated={load} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && uoms.length === 0 ? (
          <EmptyState title="No units of measure yet" testId="uom-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {uoms.map((u) => (
                <TableRow key={u.id} data-testid={`uom-row-${u.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{u.code}</TableCell>
                  <TableCell className="text-foreground">{u.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={u.is_active} disabled={!canManage} onCheckedChange={() => toggleActive(u)} data-testid={`uom-active-toggle-${u.code}`} />
                      <Badge variant={u.is_active ? "default" : "secondary"}>{u.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateUomDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/masters/uom", form);
      toast.success(`UoM ${form.code} created`);
      setForm({ code: "", name: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-uom-button"><Plus className="h-4 w-4" /> New UoM</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Unit of Measure</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-uom-form">
          <div>
            <Label>Code</Label>
            <Input required data-testid="uom-code-input" value={form.code} onChange={set("code")} className="mt-1.5" placeholder="NOS" />
            <FieldError error={errors.code} />
          </div>
          <div>
            <Label>Name</Label>
            <Input required data-testid="uom-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="Numbers" />
            <FieldError error={errors.name} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-uom-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
